import { Injectable } from '@angular/core';
import type { CommandPaletteEntry } from '../core/command-palette.logic';

/**
 * Palette commands contributed by an always-present shell component (user menu, toolbar).
 *
 * `build` runs every time the palette opens, so titles and availability read current state.
 */
export interface AppCommandProvider {
    build: () => CommandPaletteEntry[];
}

/**
 * Registry of app-wide palette commands. Unlike page commands these are not tied to a
 * workspace tab: every provider registered by the active layout contributes on every page.
 */
@Injectable({ providedIn: 'root' })
export class AppCommandsService {
    private readonly _providers = new Set<AppCommandProvider>();

    /** @returns unregister callback; call it from the component's ngOnDestroy. */
    register(provider: AppCommandProvider): () => void {
        this._providers.add(provider);
        return () => {
            this._providers.delete(provider);
        };
    }

    activeCommands(): CommandPaletteEntry[] {
        const commands: CommandPaletteEntry[] = [];
        for (const provider of this._providers) {
            try {
                commands.push(...provider.build());
            } catch {
                // One broken provider must not hide the others or stop the palette from opening.
            }
        }
        return commands;
    }
}
